import React from 'react'
import PropTypes from 'prop-types'
import { difference, cond, always, equals, isEmpty, and } from 'ramda'
import RateLimitTitle from './components/rate-limit-title'
import RateLimitRecord from './components/rate-limit-record'
import RateLimitWarning from './components/rate-limit-warning'
import RateLimitModal from './components/rate-limit-modal'

const AVAILABLE_LIMITS = ['SEND_TWEET', 'SEND_DM', 'SEND_DARK_TWEET']
const DEFAULT_LIMIT = 2

class ConfigureRateLimits extends React.Component {
  constructor (props, context) {
    super(props, context)

    this.state = {
      isPristine: props.isPristine,
      SEND_TWEET: false,
      SEND_DM: false,
      SEND_DARK_TWEET: false,
      rateLimits: props.rateLimits,
      show: false,
      showWarning: false
    }

    this.setActiveLimits = this.setActiveLimits.bind(this)
    this.handleToggle = this.handleToggle.bind(this)
    this.addRateLimit = this.addRateLimit.bind(this)
    this.removeRateLimit = this.removeRateLimit.bind(this)
    this.handleLimitChange = this.handleLimitChange.bind(this)
    this.getCurrentLimit = this.getCurrentLimit.bind(this)
    this.handleShow = this.handleShow.bind(this)
    this.handleClose = this.handleClose.bind(this)

    this.handleWarningShow = this.handleWarningShow.bind(this)
    this.handleWarningHide = this.handleWarningHide.bind(this)
  }

  handleShow () {
    this.setState({ show: true, showWarning: false })
  }

  handleClose () {
    this.setState({ show: false })
  }

  handleWarningShow () {
    this.setState({ showWarning: true })
  }

  handleWarningHide () {
    this.props.handleReset(this.props.ownerId)
    this.setState({ showWarning: false, isPristine: true })
  }

  setActiveLimits (rateLimits) {
    const setLimits = rateLimits.map(limit => {
      this.setState({
        [limit.actionType]: true
      })
      return limit.actionType
    })
    const excludedLimits = difference(AVAILABLE_LIMITS, setLimits)

    excludedLimits.forEach(excludedLimit => {
      this.setState({
        [excludedLimit]: false
      })
    })
  }

  handleToggle (e, actionType) {
    this.setState({
      isPristine: false
    })
    if (e) {
      this.handleWarningShow()
      this.addRateLimit(actionType)
      return
    }
    this.removeRateLimit(actionType)
  }

  handleLimitChange (e, actionType) {
    this.setState({
      isPristine: false
    })
    const newLimits = this.state.rateLimits.map(limit => {
      if (limit.actionType === actionType) {
        return {
          ...limit,
          requestsPerSecond: e
        }
      }
      return limit
    })
    this.setState({
      rateLimits: newLimits
    })
  }

  addRateLimit (actionType) {
    const endPoint = cond([
      [equals('SEND_TWEET'), always('statuses/update')],
      [equals('SEND_DARK_TWEET'), always('statuses/update')],
      [equals('SEND_DM'), always('direct_messages/events/new')]
    ])(actionType)

    const limit = [
      {
        userId: this.props.ownerId,
        actionType: actionType,
        platform: 'twitter',
        method: 'POST',
        endpoint: endPoint,
        requestsPerSecond: 2,
        expires: this.props.endDate
      }
    ]
    const newLimits = this.state.rateLimits.concat(limit)
    this.setState({
      [actionType]: true,
      rateLimits: newLimits
    })
  }

  removeRateLimit (actionType) {
    this.setState({
      [actionType]: false
    })

    const remainingLimits = this.state.rateLimits.filter(
      limit => limit.actionType !== actionType
    )

    this.setState({
      rateLimits: remainingLimits
    })
  }

  async componentWillReceiveProps (nextProps) {
    if (nextProps.rateLimits !== this.state.rateLimits) {
      this.setState({
        rateLimits: nextProps.rateLimits,
        isPristine: true
      })
      this.setActiveLimits(nextProps.rateLimits)
    }
  }

  getCurrentLimit (actionType, rateLimits) {
    let requestsPerSecond = 2
    rateLimits.forEach(limit => {
      if (limit.actionType === actionType) {
        requestsPerSecond = limit.requestsPerSecond
      }
    })
    return Number(requestsPerSecond)
  }

  componentDidMount () {
    this.setActiveLimits(this.props.rateLimits)
  }
  render () {
    return (
      <div className='form-group darkPanel'>
        <div className='col-md-12'>
          <div className='portlet light'>
            <RateLimitTitle />
            <div className='portlet-body'>
              <RateLimitWarning />
              {AVAILABLE_LIMITS.map(limit => {
                return (
                  <RateLimitRecord
                    key={limit}
                    type={cond([
                      [equals('SEND_TWEET'), always('Tweet')],
                      [equals('SEND_DM'), always('DM')],
                      [equals('SEND_DARK_TWEET'), always('Dark Tweet')]
                    ])(limit)}
                    active={this.state[limit]}
                    defaultLimit={DEFAULT_LIMIT}
                    currentLimit={
                      this.state.rateLimits
                        ? this.getCurrentLimit(limit, this.state.rateLimits)
                        : 2
                    }
                    handleToggle={e => this.handleToggle(e, limit)}
                    handleLimitChange={e => this.handleLimitChange(e, limit)}
                  />
                )
              })}
              <div className='form-group'>
                <button
                  className='btn green-haze pull-right'
                  disabled={this.state.isPristine}
                  style={{ width: 150 }}
                  onClick={e => {
                    this.props.handleSave(
                      this.props.ownerId,
                      this.state.rateLimits
                    )
                    e.preventDefault()
                    this.setState({
                      isPristine: true
                    })
                  }}
                >
                  Save rate-limits
                </button>
              </div>
              <div className='form-group'>
                <button
                  disabled={and(
                    isEmpty(this.state.rateLimits),
                    equals(this.state.rateLimits, this.props.rateLimits)
                  )}
                  className='btn green-haze pull-right'
                  style={{ width: 150 }}
                  onClick={e => {
                    this.props.handleReset(this.props.ownerId)
                    e.preventDefault()
                    this.setState({
                      isPristine: true
                    })
                  }}
                >
                  Reset
                </button>
              </div>
              <RateLimitModal
                showModal={this.state.showWarning}
                hideModal={this.handleWarningHide}
                handleAccept={this.handleShow}
              />
            </div>
          </div>
        </div>
      </div>
    )
  }
}

ConfigureRateLimits.propTypes = {
  rateLimits: PropTypes.array,
  endDate: PropTypes.number,
  ownerId: PropTypes.string,
  handleSave: PropTypes.func,
  handleReset: PropTypes.func,
  isPristine: PropTypes.bool
}

export default ConfigureRateLimits



// WEBPACK FOOTER //
// ./src/pages/widgets/form/configure-rate-limits/configure-rate-limits.js